import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, ActivityIndicator, Alert } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { useDb } from '../../db/client';
import { loadOrCreateProfile } from '../../features/onboarding/profileBootstrap';
import { clearOnboardingPending } from '../../features/onboarding/onboardingFlag';
import { updateRecord } from '../../db/writes';
import { profiles } from '../../db/schema';
import { SyncServiceImpl } from '../../sync';
import { parseLocaleNumber } from '../shared/parseLocaleNumber';
import { colors, spacing } from '../theme';
import { Screen } from '../shared/Screen';
import { Heading, Subtitle } from '../shared/Typography';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';

// Uma única instância reaproveitada, mesmo padrão de app/(tabs)/perfil/index.tsx.
const syncService = new SyncServiceImpl();

type Step = 'hourlyRate' | 'markup';

/**
 * Mini-onboarding pós-cadastro (spec S01, Etapa 9): duas perguntas — valor
 * da hora e mark-up padrão —, cada uma com "decidir depois" (grava 0, sem
 * forçar preenchimento). Roda uma vez, alcançada só via redirect do
 * auth-gate (app/_layout.tsx) quando `isOnboardingPending(user.id)` é
 * verdadeiro; ver src/features/onboarding/onboardingFlag.ts para quando
 * isso acontece.
 */
export function OnboardingScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();
  const { db, ready } = useDb();

  const [step, setStep] = useState<Step>('hourlyRate');
  const [hourlyRateText, setHourlyRateText] = useState('');
  const [markupText, setMarkupText] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const bootstrapProfile = useCallback(async () => {
    if (!ready || !db || !user) return;
    setLoadingProfile(true);
    setLoadError(false);
    try {
      // Garante que existe uma linha de `profiles` local antes de tentar
      // editá-la (ver profileBootstrap.ts): o trigger do Postgres cria a
      // linha no signup, mas ela só chega aqui depois de um pull.
      await loadOrCreateProfile(db, user.id, () => syncService.pullAll(db));
    } catch (error) {
      console.error('OnboardingScreen: falha ao preparar perfil local', error);
      setLoadError(true);
    } finally {
      setLoadingProfile(false);
    }
  }, [ready, db, user]);

  useEffect(() => {
    bootstrapProfile();
  }, [bootstrapProfile]);

  const finishOnboarding = useCallback(
    async (hourlyRate: number, defaultMarkupPct: number) => {
      if (!db || !user) return;
      setSubmitting(true);
      try {
        await updateRecord(db, profiles, user.id, { hourlyRate, defaultMarkupPct });
        await clearOnboardingPending(user.id);
        router.replace('/(tabs)');
      } catch (error) {
        console.error('OnboardingScreen: falha ao salvar respostas', error);
        Alert.alert(t('onboarding.errorTitle'), t('onboarding.saveErrorMessage'));
      } finally {
        setSubmitting(false);
      }
    },
    [db, user, router, t]
  );

  const handleHourlyRateNext = () => {
    const value = parseLocaleNumber(hourlyRateText);
    if (value === null) {
      Alert.alert(t('onboarding.errorTitle'), t('onboarding.invalidNumberMessage'));
      return;
    }
    setHourlyRateText(String(value));
    setStep('markup');
  };

  const handleHourlyRateSkip = () => {
    setHourlyRateText('0');
    setStep('markup');
  };

  const handleMarkupFinish = () => {
    const hourlyRate = parseLocaleNumber(hourlyRateText) ?? 0;
    const markupValue = parseLocaleNumber(markupText);
    if (markupValue === null) {
      Alert.alert(t('onboarding.errorTitle'), t('onboarding.invalidNumberMessage'));
      return;
    }
    finishOnboarding(hourlyRate, markupValue);
  };

  const handleMarkupSkip = () => {
    const hourlyRate = parseLocaleNumber(hourlyRateText) ?? 0;
    finishOnboarding(hourlyRate, 0);
  };

  if (!ready || loadingProfile) {
    return (
      <Screen>
        <ActivityIndicator size="large" color={colors.sageDeep} />
        <Subtitle>{t('onboarding.loading')}</Subtitle>
      </Screen>
    );
  }

  if (loadError) {
    return (
      <Screen>
        <Subtitle>{t('onboarding.loadErrorMessage')}</Subtitle>
        <Button title={t('onboarding.hourlyRate.next')} onPress={bootstrapProfile} />
      </Screen>
    );
  }

  if (submitting) {
    return (
      <Screen>
        <ActivityIndicator size="large" color={colors.sageDeep} />
      </Screen>
    );
  }

  if (step === 'hourlyRate') {
    return (
      <Screen>
        <Heading>{t('onboarding.hourlyRate.question')}</Heading>
        <Subtitle>{t('onboarding.hourlyRate.explanation')}</Subtitle>
        <TextField
          style={styles.input}
          placeholder={t('onboarding.hourlyRate.placeholder')}
          value={hourlyRateText}
          onChangeText={setHourlyRateText}
          keyboardType="decimal-pad"
        />
        <Button title={t('onboarding.hourlyRate.next')} onPress={handleHourlyRateNext} />
        <Button title={t('onboarding.hourlyRate.skip')} variant="ghost" onPress={handleHourlyRateSkip} />
      </Screen>
    );
  }

  return (
    <Screen>
      <Heading>{t('onboarding.markup.question')}</Heading>
      <Subtitle>{t('onboarding.markup.explanation')}</Subtitle>
      <TextField
        style={styles.input}
        placeholder={t('onboarding.markup.placeholder')}
        value={markupText}
        onChangeText={setMarkupText}
        keyboardType="decimal-pad"
      />
      <Button title={t('onboarding.markup.finish')} onPress={handleMarkupFinish} />
      <Button title={t('onboarding.markup.skip')} variant="ghost" onPress={handleMarkupSkip} />
      <Button title={t('onboarding.markup.back')} variant="ghost" onPress={() => setStep('hourlyRate')} />
    </Screen>
  );
}

const styles = StyleSheet.create({
  input: { fontSize: 18, textAlign: 'center', marginBottom: spacing.md },
});
